import { type Coord } from "./types";

export type PhysicsObjectOptions = {
  gravity?: number,
  bounciness?: number,
  stickiness?: number,
  airFriction?: number,
  angularAirFriction?: number,
  initialX?: number,
  initialY?: number,
  initialSpeedX?: number,
  initialSpeedY?: number,
  initialAngle?: number,
  initialAngularSpeed?: number,
}

export class PhysicsObject {
  gravity: number;
  bounciness: number;
  stickiness: number;
  airFriction: number;
  angularAirFriction: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  angle: number;
  angularVelocity: number;
  isStopped: boolean;
  animationId?: number;
  previousCoord: Coord[];
  howManyCoordsBack: number;
  grabOffsetX?: number;
  grabOffsetY?: number;
  dragOverlay?: HTMLDivElement;
  isGrabbed: boolean;

  constructor(public element: HTMLElement, options: PhysicsObjectOptions = {}) {
    if (options.stickiness && options.stickiness < 0.034) {
      throw new Error("Stickiness below 0.34 leads to a physics bug");
    }

    this.gravity = options.gravity ?? 0.1;
    this.bounciness = options.bounciness ?? 0.4;
    this.stickiness = options.stickiness ?? 0.2;
    this.airFriction = options.airFriction ?? 0.0001;
    this.angularAirFriction = options.angularAirFriction ?? 0.005;

    this.x = options.initialX ?? 0;
    this.y = options.initialY ?? 0;
    this.vx = options.initialSpeedX ?? 0;
    this.vy = options.initialSpeedY ?? 0;
    this.angle = options.initialAngle ?? 0;
    this.angularVelocity = options.initialAngularSpeed ?? 0;

    this.isStopped = false;

    this.previousCoord = [];
    this.howManyCoordsBack = 3;
    this.isGrabbed = false

    this.element.onmousedown = (e) => {
      e.preventDefault();
      this.grab(e);
    };

    this.start();
  }

  grab(event?: MouseEvent) {
    if (!event) {
      this.grabOffsetX = 0.5;
      this.grabOffsetY = 0.5;
    } else {
      this.grabOffsetX = (event.clientX - this.x) / this.element.offsetWidth;
      this.grabOffsetY = (event.clientY - this.y) / this.element.offsetHeight;
    }

    this.dragOverlay = document.createElement("div");
    this.dragOverlay.className = "drag-overlay";
    document.body.appendChild(this.dragOverlay);

    this.dragOverlay.onmouseup = this.drop.bind(this);
    this.dragOverlay.onmouseleave = this.drop.bind(this);
    this.dragOverlay.onmousemove = this.onGrabMove.bind(this);
    this.isGrabbed = true;
  }

  onGrabMove(event: MouseEvent) {
    this.x = event.clientX - this.element.offsetWidth * this.grabOffsetX!;
    this.y = event.clientY - this.element.offsetHeight * this.grabOffsetY!;
    this.previousCoord.push({ x: this.x, y: this.y });
    if (this.previousCoord.length === this.howManyCoordsBack)
      this.previousCoord.shift();
  }

  drop() {
    this.isGrabbed = false;
    this.dragOverlay?.remove();
    this.grabOffsetX = undefined;
    this.grabOffsetY = undefined;
    this.previousCoord = [];
  }

  update() {
    if (this.isStopped) return;

    if (this.isGrabbed) {
      const lastX = this.previousCoord[0]?.x ?? this.x;
      const lastY = this.previousCoord[0]?.y ?? this.y;
      this.vx = this.x - lastX;
      this.vy = this.y - lastY;

      // Spin from the tangential component of the cursor motion around the grab point
      const rx = (this.grabOffsetX ?? 0.5) * this.element.offsetWidth;
      const ry = (this.grabOffsetY ?? 0.5) * this.element.offsetHeight;
      const r2 = rx * rx + ry * ry;
      this.angularVelocity = r2 > 0 ? (rx * this.vy - ry * this.vx) / r2 : 0;
    } else {
      this.vy += this.gravity;
      this.x += this.vx;
      this.y += this.vy;

      this.vy *= 1 - this.airFriction;
      this.vx *= 1 - this.airFriction;

      this.angularVelocity *= 1 - this.angularAirFriction;
    }

    this.angle += this.angularVelocity;
    if (this.angle > Math.PI || this.angle < -Math.PI) {
      this.angle = ((this.angle + Math.PI) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI) - Math.PI;
    }

    this.checkCollisions();

    this.render();
  }

  checkCollisions() {
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    // --- FLOOR COLLISION ---
    if (this.y + this.element.offsetHeight >= windowHeight) {
      if (this.isGrabbed) this.drop();

      const depthOfPit = this.y + this.element.offsetHeight - windowHeight;

      this.vy = -this.vy * this.bounciness;
      this.vx *= 1 - this.stickiness; // Reduce X speed depending on stickiness
      this.angularVelocity *= 1 - this.stickiness; // Spin loss on impact
      this.angularVelocity += this.stickiness * this.vx / (this.element.offsetWidth / 2); // Rolling coupling
      this.y -= depthOfPit * 2; // Invert position of pit because of bounce

      // Technically I should split the bounce in two parts, before and after the collision,
      // since the gravity should slow the object on the second part

      // It would fix the issue with low stickiness (see ctor), but leads to
      // even more bugs because of numeric imprecision of JS
    }

    // For the 3 other collision, no comments
    // No stickiness calculation (Would be funny tho)

    // --- CEILING COLLISION ---
    if (this.y <= 0) {
      if (this.isGrabbed) this.drop();
      const depthOfPit = -this.y;
      this.vy = -this.vy * this.bounciness;
      this.vx *= 1 - this.stickiness;
      this.angularVelocity *= 1 - this.stickiness;
      this.angularVelocity -= this.stickiness * this.vx / (this.element.offsetWidth / 2);
      this.y += depthOfPit * 2;
    }

    // --- LEFT WALL COLLISION ---
    if (this.x <= 0) {
      if (this.isGrabbed) this.drop();
      const depthOfPit = -this.x;
      this.vx = -this.vx * this.bounciness;
      this.vy *= 1 - this.stickiness;
      this.angularVelocity *= 1 - this.stickiness;
      this.angularVelocity += this.stickiness * this.vy / (this.element.offsetHeight / 2);
      this.x += depthOfPit * 2;
    }

    // --- RIGHT WALL COLLISION ---
    if (this.x + this.element.offsetWidth >= windowWidth) {
      if (this.isGrabbed) this.drop();
      const depthOfPit = this.x + this.element.offsetWidth - windowWidth;
      this.vx = -this.vx * this.bounciness;
      this.vy *= 1 - this.stickiness;
      this.angularVelocity *= 1 - this.stickiness;
      this.angularVelocity -= this.stickiness * this.vy / (this.element.offsetHeight / 2);
      this.x -= depthOfPit * 2;
    }
  }

  freezeRenderLoop() {
    this.vx = 0;
    this.vy = 0;
    this.angularVelocity = 0;
    this.isStopped = true;
    this.render();
    if (this.animationId) cancelAnimationFrame(this.animationId);
  }

  render() {
    const rotationInDeg = (this.angle * 180) / Math.PI;
    this.element.style.transform = `translate3d(${this.x}px, ${this.y}px, 0) rotate(${rotationInDeg}deg)`;
  }

  start() {
    const loop = () => {
      this.update();
      if (!this.isStopped) {
        this.animationId = requestAnimationFrame(loop);
      }
    };
    this.animationId = requestAnimationFrame(loop);
  }

  stop() {
    this.freezeRenderLoop();
    this.element.remove();
    this.dragOverlay?.remove();
    return { x: this.x, y: this.y };
  }
}
